import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import {
  LayoutDashboard, ShoppingCart, Package, Factory, ClipboardList,
  Users, Shield, Key, LogOut, Menu, X, UserCircle, Building2, FileText,
  ShoppingBag, Truck, CheckCircle, FileX, DollarSign, Settings2, Clock,
  Activity, Cpu, Layers, Timer, AlertTriangle, Ban, BarChart3, Eye, Settings, Wrench,
  GitBranch, FileSpreadsheet, ListTodo, Download, Ruler, Calculator
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [currency, setCurrency] = React.useState(null);

  React.useEffect(() => {
    api.get('/config/currency').then((r) => setCurrency(r.data)).catch(() => {});
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const salesNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/sales-orders', icon: ShoppingCart, label: 'Sales Orders' },
    { path: '/packing', icon: ClipboardList, label: 'Packing Lists' },
    { path: '/stock-report', icon: BarChart3, label: 'Stock Report' },
  ];

  const purchaseNavItems = [
    { path: '/purchase/indents', icon: FileText, label: 'Material Indents' },
    { path: '/purchase/orders', icon: ShoppingBag, label: 'Purchase Orders' },
    { path: '/purchase/mrn', icon: Truck, label: 'MRN' },
    { path: '/purchase/qc', icon: CheckCircle, label: 'QC Inspection' },
    { path: '/purchase/debit-notes', icon: FileX, label: 'Debit Notes' },
  ];

  const productionPlanningItems = [
    { path: '/production/orders', icon: ClipboardList, label: 'Production Orders' },
    { path: '/production/material-requirements', icon: Calculator, label: 'Material Requirements' },
  ];

  const productionExecutionItems = [
    { path: '/production/live-dashboard', icon: Activity, label: 'Live Dashboard' },
    { path: '/production/entry', icon: Layers, label: 'Production Entry' },
    { path: '/production/downtime', icon: AlertTriangle, label: 'Downtime Entry' },
  ];

  const productionMastersItems = [
    { path: '/production/machines', icon: Cpu, label: 'Machines' },
    { path: '/production/process-master', icon: GitBranch, label: 'Process Master' },
    { path: '/production/bom', icon: Layers, label: 'BOM' },
    { path: '/production/routing', icon: Wrench, label: 'Routing' },
    { path: '/production/cycle-times', icon: Timer, label: 'Cycle Times' },
    { path: '/production/downtime-reasons', icon: AlertTriangle, label: 'Downtime Reasons' },
    { path: '/production/rejection-reasons', icon: Ban, label: 'Rejection Reasons' },
  ];

  const productionReportItems = [
    { path: '/production/daily-summary', icon: BarChart3, label: 'Daily Summary' },
    { path: '/production/supervisor', icon: Eye, label: 'Supervisor Dashboard' },
    { path: '/production/dispatch', icon: Truck, label: 'Dispatch' },
    { path: '/production/settings', icon: Settings, label: 'Settings' },
  ];

  const mastersNavItems = [
    { path: '/masters/customers', icon: UserCircle, label: 'Customers' },
    { path: '/masters/vendors', icon: Building2, label: 'Vendors' },
    { path: '/masters/departments', icon: Factory, label: 'Departments' },
    { path: '/masters/shifts', icon: Clock, label: 'Shifts' },
    { path: '/masters/plants', icon: Building2, label: 'Plants' },
    { path: '/inventory', icon: Package, label: 'SKU / Inventory' },
    { path: '/masters/units', icon: Ruler, label: 'Unit Master' },
  ];

  const tallyNavItems = [
    { path: '/tally/dashboard', icon: LayoutDashboard, label: 'Tally Dashboard' },
    { path: '/tally/outstanding', icon: DollarSign, label: 'Outstanding' },
    { path: '/tally/config', icon: Shield, label: 'Tally Config' },
  ];

  const adminNavItems = [
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/roles', icon: Shield, label: 'Roles' },
    { path: '/admin/custom-fields', icon: Settings2, label: 'Custom Fields' },
    { path: '/admin/currency', icon: DollarSign, label: 'Currency' },
    { path: '/admin/license', icon: Key, label: 'License' },
  ];

  const sectionStyle = { padding: '12px 20px 4px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary)', fontWeight: 600 };
  const subSectionStyle = { padding: '4px 20px 2px', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)', fontWeight: 500, opacity: 0.7 };

  const renderNavItems = (items, testIdPrefix) => (
    <nav className="sidebar-nav" style={{ padding: '4px 0' }} data-testid={`${testIdPrefix}-nav`}>
      {items.map((item) => (
        <NavLink key={item.path} to={item.path}
          className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
          data-testid={`nav-${item.label.toLowerCase().replace(/[\s/]/g, '-')}`}
        >
          <item.icon size={16} strokeWidth={1.5} />
          <span style={{ fontSize: '13px' }}>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="App">
      {sidebarOpen && (
        <aside className="sidebar" data-testid="sidebar">
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-default)' }}>
            {/* <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 900 }}>ERP System</h2>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--text-tertiary)' }}>Production-Ready</p> */}
            <img src='https://server.dweb.co.in/img/cpp.png' alt='logo' style={{width:'60%', margin:'auto'}} />
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            <div style={sectionStyle}>Sales Module</div>
            {renderNavItems(salesNavItems, 'sales')}

            <div style={sectionStyle}>Purchase Module</div>
            {renderNavItems(purchaseNavItems, 'purchase')}

            <div style={{ ...sectionStyle, background: 'var(--brand-subtle)', margin: '8px 0 0', padding: '10px 20px 4px', borderTop: '1px solid var(--border-default)' }}>
              <span style={{ color: 'var(--brand-primary)' }}>Production Module</span>
            </div>
            <div style={subSectionStyle}>Planning</div>
            {renderNavItems(productionPlanningItems, 'prod-plan')}
            <div style={subSectionStyle}>Execution</div>
            {renderNavItems(productionExecutionItems, 'prod-exec')}
            <div style={subSectionStyle}>Production Masters</div>
            {renderNavItems(productionMastersItems, 'prod-masters')}
            <div style={subSectionStyle}>Reports & Config</div>
            {renderNavItems(productionReportItems, 'prod-reports')}

            <div style={sectionStyle}>Masters</div>
            {renderNavItems(mastersNavItems, 'masters')}

            <div style={sectionStyle}>Tally Integration</div>
            {renderNavItems(tallyNavItems, 'tally')}

            <div style={sectionStyle}>Workflow & QC</div>
            {renderNavItems([
              { path: '/workflow', icon: GitBranch, label: 'Workflows & Approvals' },
              { path: '/specifications', icon: FileSpreadsheet, label: 'Specifications & QC' },
              { path: '/task-queue', icon: ListTodo, label: 'Task Queue' },
            ], 'workflow')}

            <div style={sectionStyle}>Administration</div>
            {renderNavItems(adminNavItems, 'admin')}
          </div>
        </aside>
      )}

      <div className="main-content" style={{ marginLeft: sidebarOpen ? '240px' : '0' }}>
        <header className="top-header" data-testid="top-header">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="btn btn-ghost btn-sm" data-testid="toggle-sidebar-btn">
              {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <h1 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>ERP System</h1>
          </div>
          <div className="flex items-center gap-4">
            <NavLink to="/admin/currency" title="Currency settings" data-testid="header-currency"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '6px',
                border: '1px solid var(--border-default)', textDecoration: 'none', color: 'var(--text-secondary)' }}>
              <DollarSign size={14} />
              <span style={{ fontSize: '12px', fontWeight: 600 }}>{currency?.base_currency || 'UGX'}</span>
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>1 USD = {currency ? Number(currency.usd_to_ugx_rate).toLocaleString() : '—'}</span>
            </NavLink>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '14px', fontWeight: 500 }} data-testid="user-name">{user?.name}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }} data-testid="user-email">{user?.email}</div>
            </div>
            <button onClick={handleLogout} className="btn btn-secondary btn-sm" data-testid="logout-btn">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </header>
        <main className="content-area" data-testid="main-content">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
